import { computed, ComputedRef, Ref, watch, ref } from 'vue';
import { CODES, useLiveQueryV2 } from '@libs/use';
import { OptionContract } from '@libs/api-reference';
import {
  SYMBOL_TRADE_CHANNEL_PREFIX,
  transformTradePriceChange,
} from '@core/symbol';

import * as API from '../requests';
import { CONTRACT_INFO_CHANGED_CHANNEL, OPTION_QUERY_KEYS } from '../constants';
import { transformOptionContractChanged } from '../utils/transform/optionContractChanged';

export default function useLiveGetOptionContract({
  isin,
  immediate = true,
}: {
  isin: Ref<string>;
  immediate?: ComputedRef<boolean> | boolean;
}) {
  const {
    result,
    status: getOptionContractStatus,
    execute: getOptionContract,
    isPending: isLoadingOptionContract,
    isSuccess: isSuccessLoadingOptionContract,
    isError: isErrorLoadingOptionContract,
  } = useLiveQueryV2<OptionContract>({
    fetcher: () => API.getOptionContract(isin.value),
    key: OPTION_QUERY_KEYS.optionContract(isin),
    immediate,
    cacheTime: 1000 * 60, // 1m
    staleTime: 1000 * 60, // 1m
    getChannels(result) {
      return [
        `${SYMBOL_TRADE_CHANNEL_PREFIX}${result?.data.underlyingAssetIsin}`,
        `${CONTRACT_INFO_CHANGED_CHANNEL}${result?.data.isin}`,
      ];
    },
    unsubscribe: false,
    onUpdate(signal) {
      const actions = {
        [CODES.OptionContractChanged]: transformOptionContractChanged,
        [CODES.SymbolTradePriceChangeSignal]: transformTradePriceChange,
      };

      if (actions[signal.code] && result.value?.data) {
        const data: ReturnType<
          typeof transformOptionContractChanged &
            typeof transformTradePriceChange
        > = actions[signal.code](signal.data);

        if (signal.code === CODES.OptionContractChanged) {
          Object.assign(result.value.data, data);
        } else {
          Object.assign(result.value.data, {
            underlyingAssetLastTradePrice: data?.lastTradePrice,
            underlyingAssetReferencePriceVariationPercentage:
              data?.referencePriceVariationPercentage,
          });
        }
      }
    },
  });

  const optionContract = computed(() => result.value?.data);

  return {
    optionContract,
    getOptionContract,
    getOptionContractStatus,
    isLoadingOptionContract,
    isSuccessLoadingOptionContract,
    isErrorLoadingOptionContract,
  };
}
